import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { FonteInput } from '../types/fonte';
import { toast } from 'react-toastify';

export function useFontes() {
  const queryClient = useQueryClient();

  // Bucket e caminho onde estão as fontes
  const bucket = 'assets';
  const path = 'fonts';

  // Query para listar fontes
  const { data: fontes, isLoading: listLoading } = useQuery({
    queryKey: ['fontes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fontes')
        .select('*')
        .order('nome', { ascending: true });

      if (error) throw error;
      return data || [];
    },
  });

  // Query para buscar fonte específica
  const useFonte = (id: string) => {
    return useQuery({
      queryKey: ['fontes', id],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('fontes')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        return data;
      },
      enabled: !!id,
    });
  };

  // Mutation para criar fonte
  const { mutateAsync: criarFonte } = useMutation({
    mutationFn: async (fonte: FonteInput) => {
      const { error } = await supabase
        .from('fontes')
        .insert([fonte]);

      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fontes'] });
      toast.success('Fonte criada com sucesso!');
    },
    onError: (error: any) => {
      toast.error('Erro ao criar fonte: ' + error.message);
    },
  });

  // Mutation para atualizar fonte
  const { mutateAsync: atualizarFonte } = useMutation({
    mutationFn: async ({ id, fonte }: { id: string; fonte: FonteInput }) => {
      const { error } = await supabase
        .from('fontes')
        .update(fonte)
        .eq('id', id);

      if (error) throw error;
      return true;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['fontes'] });
      queryClient.invalidateQueries({ queryKey: ['fontes', variables.id] });
      toast.success('Fonte atualizada com sucesso!');
    },
    onError: (error: any) => {
      toast.error('Erro ao atualizar fonte: ' + error.message);
    },
  });

  // Mutation para excluir fonte
  const { mutateAsync: excluirFonte } = useMutation({
    mutationFn: async (id: string) => {
      // Primeiro, buscar o nome do arquivo antes de excluir a fonte
      const { data: fonte, error: fetchError } = await supabase
        .from('fontes')
        .select('arquivo')
        .eq('id', id)
        .single();
      
      if (fetchError) {
        console.error('[excluirFonte] Erro ao buscar informações da fonte:', fetchError);
        throw fetchError;
      }
      
      if (!fonte) {
        throw new Error('Fonte não encontrada');
      }
      
      // Excluir do banco de dados
      const { error: deleteError } = await supabase
        .from('fontes')
        .delete()
        .eq('id', id);
      
      if (deleteError) {
        console.error('[excluirFonte] Erro ao excluir fonte do banco:', deleteError);
        throw deleteError;
      }
      
      // Só excluir o arquivo do storage se não houver outra fonte vinculada a ele
      if (fonte.arquivo) {
        // Verifica se existe outra fonte usando o mesmo arquivo
        const { count, error: countError } = await supabase
          .from('fontes')
          .select('id', { count: 'exact', head: true })
          .eq('arquivo', fonte.arquivo);
        
        if (countError) {
          console.error('[excluirFonte] Erro ao contar fontes vinculadas ao arquivo:', countError);
        } else if ((count ?? 0) === 0) {
          // Se não existe mais nenhuma fonte usando esse arquivo, pode remover do storage
          const { error: storageError } = await supabase.storage
            .from(bucket)
            .remove([`${path}/${fonte.arquivo}`]);
          
          if (storageError) {
            console.error('[excluirFonte] Erro ao excluir arquivo do storage:', storageError);
            // Não lançamos erro aqui pois a exclusão do banco já foi feita
            // Apenas logamos o erro para debug
          }
        }
      }
      
      return true;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['fontes'] });
      queryClient.invalidateQueries({ queryKey: ['fontes', id] });
      toast.success('Fonte excluída com sucesso!');
    },
    onError: (error: any) => {
      toast.error('Erro ao excluir fonte: ' + error.message);
    },
  });

  return {
    fontes,
    listLoading,
    useFonte,
    criarFonte,
    atualizarFonte,
    excluirFonte,
  };
} 